import { useDispatch, useSelector } from 'react-redux'
import { routeTo } from "../application/actions/ui";
import { Block } from "baseui/block";
import { LabelSmall, HeadingLarge } from "baseui/typography";
import ReDoMenuCard from "./ReDoMenuCard";
import { useEffect, useState } from "react";
import { loadChallengeById } from '../application/actions/challenges';
import { loadInboxes, submitInbox } from "../application/actions/inbox";
import { loadNotifications } from '../application/actions/notifications';
import { loadSolutionById } from '../application/actions/solutions';
import { ChevronDown } from 'baseui/icon';
import { ANCHOR, Drawer, SIZE } from 'baseui/drawer';
import { useStyletron } from 'baseui';
import { getNotifications } from '../application/selectors/notifications';
import { getInbox } from '../application/selectors/inbox';
import useLocale from '../hooks/useLocale';


const ReDoNotifications = ({ noHeading, onChatClick }) => {
  const dispatch = useDispatch();
  const { t } = useLocale();
  const notifications = useSelector(getNotifications);
  const inboxes = useSelector(getInbox);
  const [, theme] = useStyletron();
  const [isOpen, setIsOpen] = useState({});

  const isWeb = !!onChatClick

  const onInboxClick = (ev, e) => {
    ev.stopPropagation();
    
    dispatch(submitInbox({ recipient: e.recipientID, project: e.projectID, projectType: e.projectType })).then(r => {
      
      onChatClick ? onChatClick({ item: r }) : dispatch(routeTo({ view: 'chat', payload: { item: r } }))
    })
  }

  const onNotificationClick = (ev, e) => {
    ev.stopPropagation();

    // some notifications (e.g. comment/reply) carry the project kind in data.type, others in data.projectType
    // — mirror the same fallback the title mapping uses in middleware/notifications.js
    const projectType = e.data?.projectType ?? e.data?.type
    // backend serializes IDs with a capital suffix (projectID); fall back to other casings just in case
    const projectId   = e.data?.projectID ?? e.data?.projectId
    // derive the action type from the last segment: notification_vote → 'vote'
    const notifType   = e.type?.split('_').slice(1).join('_')

    if (projectType === 'solution' || projectType === 'challengeSolution') {
      if (!projectId) return
      dispatch(loadSolutionById(projectId)).then((solution) => {
        dispatch(routeTo({ view: 'solution', payload: { solution } }))
      })
    } else if (projectType === 'challenge') {
      if (!projectId) return
      dispatch(loadChallengeById(projectId)).then((challenge) => {
        dispatch(routeTo({ view: 'challenge', payload: { challenge } }))
      })
    } else if (notifType === 'inbox' || notifType === 'message') {
      const openChat = (item) => onChatClick ? onChatClick({ item }) : dispatch(routeTo({ view: 'chat', payload: { item } }))
      // message/reply notifications carry the inboxId but no project info, so open the existing
      // conversation directly from the loaded inbox list instead of re-creating it via submitInbox
      const existing = inboxes.find(i => i.id === e.data?.inboxId)
      if (existing) { openChat(existing); return }
      const recipientId = e.data?.fromID ?? e.data?.fromId ?? e.data?.userID ?? e.data?.userId ?? e.data?.senderID
      if (!recipientId) return
      dispatch(submitInbox({ recipient: recipientId, project: projectId, projectType })).then(openChat)
    }
  }

  useEffect(() => {
    dispatch(loadInboxes)
    dispatch(loadNotifications)
  }, [dispatch])


  return (
    <>
      {!isWeb &&
        <Drawer
          isOpen={!!isOpen?.elements?.length}
          autoFocus
          onClose={() => setIsOpen({})}
          anchor={ANCHOR.bottom}
          size={SIZE.default}
          overrides={{
            DrawerContainer: {
              style: {
                borderTopRightRadius: '32px',
                borderTopLeftRadius: '32px',
                height: 'calc(100vh - var(--redo-nav-bar-height, 68px) - 120px)',
                maxHeight: 'calc(100vh - var(--redo-nav-bar-height, 68px) - 120px)'
              }
            },
            DrawerBody: {
              style: {
                margin: '0 !important',
                padding: '0',
                overflow: 'hidden'
              }
            },
            Close: {
              style: {
                color: theme.colors.primary300,
                width: '48px',
                height: '48px',
              }
            }
          }}
        >
          <Block height='100%' padding='0 1ch 7ch 1ch' overflow='auto' marginTop='7ch'>
            <HeadingLarge padding='0 12px 12px 12px'>{t('notifications.all')} {isOpen.kind === 'Notifications' ? t('nav.notifications') : t('notifications.messages')}</HeadingLarge>
            {!!isOpen?.elements?.length && <ReDoMenuCard onClick={isOpen.kind === 'Notifications' ? onNotificationClick : onInboxClick} options={isOpen.elements} />}
          </Block>
        </Drawer>
      }
      <Block display='flex' gridGap='2ch' flexDirection='column' height='100%'>
        <Block>
          {!noHeading && <Block display='flex' justifyContent='space-between' alignItems='center'>
            <HeadingLarge>{t('nav.notifications')}</HeadingLarge>
            {!isWeb && <Block onClick={() => setIsOpen({ kind: 'Notifications', elements: notifications })} alignItems='center' display='flex'>
              <LabelSmall overrides={{ Block: { style: { fontWeight: '600' } } }}>{t('notifications.see_all')}</LabelSmall>
              <ChevronDown size={24} />
            </Block>}
          </Block>}
          <Block>
            <ReDoMenuCard onClick={onNotificationClick} options={isWeb ? notifications : notifications.slice(0, 3)} />
          </Block>
        </Block>

        {!noHeading &&
          <Block>
            <Block display='flex' justifyContent='space-between' alignItems='center'>
              <HeadingLarge>{t('notifications.messages')}</HeadingLarge>
              {!isWeb && <Block onClick={() => setIsOpen({ kind: 'Messages', elements: inboxes })} alignItems='center' display='flex'>
                <LabelSmall overrides={{ Block: { style: { fontWeight: '600' } } }}>{t('notifications.see_all')}</LabelSmall>
                <ChevronDown size={24} />
              </Block>}
            </Block>
            <Block>
              <ReDoMenuCard onClick={onInboxClick} options={isWeb ? inboxes : inboxes.slice(0, 3)} />
            </Block>
          </Block>
        }
      </Block>
    </>
  );
}

export default ReDoNotifications